import React from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import { splitInline } from '../parser/inline';
import { getInlineDef, registryLookup } from './registry';

/** インライントークンを対応コンポーネントに描画する */
const renderToken = (name: string, value: string, key: React.Key): React.ReactNode => {
  const def = getInlineDef(name);
  if (!def) return `{{${name}:${value}}}`;
  const Comp = def.component;
  return <Comp key={key} value={value} />;
};

/**
 * インライン限定の軽量 Markdown レンダラ。
 * `**bold**` / `*italic*` / `` `code` `` / `[label](url)` のみをインライン要素として描画する。
 * ブロック要素（見出し・リスト・段落）は生成しないため、トークンと同じ行に自然に並ぶ。
 * MDEditor.Markdown はブロックコンテナを生成し改行・レイアウト崩れの原因になるため使わない。
 */
const renderInlineMarkdown = (text: string, keyPrefix: string): React.ReactNode[] => {
  // 太字 / 斜体 / コード / リンク をまとめて 1 パスで分割する。
  const RE = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  RE.lastIndex = 0;
  while ((m = RE.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-t${i}`}>{text.slice(last, m.index)}</React.Fragment>,
      );
      i += 1;
    }
    if (m[1]) {
      // **bold**（knz の太字は通常より強く 800 で見せる）
      nodes.push(
        <Box component="strong" key={`${keyPrefix}-b${i}`} sx={{ fontWeight: 800 }}>
          {m[2]}
        </Box>,
      );
    } else if (m[3]) {
      // *italic*
      nodes.push(
        <Box component="em" key={`${keyPrefix}-i${i}`}>
          {m[4]}
        </Box>,
      );
    } else if (m[5]) {
      // `code`
      nodes.push(
        <Box
          component="code"
          key={`${keyPrefix}-c${i}`}
          sx={{
            px: 0.5,
            py: 0.1,
            mx: 0.25,
            fontSize: '0.85em',
            fontFamily: 'monospace',
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {m[6]}
        </Box>,
      );
    } else if (m[7]) {
      // [label](url)
      nodes.push(
        <Link key={`${keyPrefix}-l${i}`} href={m[9]} target="_blank" rel="noopener noreferrer">
          {m[8]}
        </Link>,
      );
    }
    i += 1;
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    nodes.push(<React.Fragment key={`${keyPrefix}-t${i}`}>{text.slice(last)}</React.Fragment>);
  }
  return nodes;
};

type Props = {
  /** 描画するテキスト（インライン Markdown + インライントークンを含む） */
  text: string;
  /** true の場合、Markdown を解釈せずインライントークンのみ処理する */
  plain?: boolean;
};

/**
 * text 内のインライン Markdown とインライントークン（`{{...}}` / `@userId`）を
 * 描画する共通コンポーネント。registry のインライン定義をホワイトリストとして使う。
 * すべてインライン要素として描画するため、トークンの前後で改行やレイアウト崩れが起きない。
 */
export const InlineText: React.FC<Props> = ({ text, plain }) => {
  const segments = splitInline(text ?? '', registryLookup.hasInline);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.kind === 'token') {
          return renderToken(seg.name, seg.value, i);
        }
        if (plain) {
          return <React.Fragment key={i}>{seg.text}</React.Fragment>;
        }
        return <React.Fragment key={i}>{renderInlineMarkdown(seg.text, String(i))}</React.Fragment>;
      })}
    </>
  );
};

export default InlineText;
